import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, tap, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { Invoice, InvoiceLineItem } from '../models/invoice';

/**
 * Coerce a money value that may have arrived as a string into a number.
 * Amount inputs are text-bound in places, and older documents can hold the string that
 * ngModel wrote back, so anything summing these fields would string-concatenate instead
 * of adding. Blank/unparseable values become undefined ("not recorded"), never NaN.
 */
export function toInvoiceAmount(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') return isNaN(value) ? undefined : value;

  const cleaned = String(value).replace(/[$,\s]/g, '');
  if (cleaned === '') return undefined;

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? undefined : parsed;
}

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  // All invoices (GET invoices). Only meaningful once allInvoicesLoaded is true - it is NOT
  // populated by per-campaign loads, so a partial list can never be mistaken for the full one.
  invoiceCache: Invoice[] = [];
  private allInvoicesLoaded = false;

  // Invoices of single campaigns (GET campaigns/{id}/invoices), loaded when a campaign's Invoices
  // tab is opened. Used only while the full list has not been loaded.
  private campaignInvoiceCache = new Map<string, Invoice[]>();

  constructor(private http: HttpClient) {}

  // One invoice of a campaign, straight from the server (no cache side effects). Used by the
  // Changes tab viewer, where the Invoices tab may never have been opened.
  getCampaignInvoice(campaignId: string, invoiceId: string): Observable<Invoice> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/invoices/${invoiceId}`;
    return this.http.get<Invoice>(url);
  }


  /**
   * Normalize the numeric fields on an invoice in place. Every page shares the cached
   * invoice instances, so doing this once on the way in keeps all consumers safe.
   */
  private normalizeAmounts(invoice: Invoice): Invoice {
    if (!invoice) return invoice;
    invoice.actualPayAmount = toInvoiceAmount(invoice.actualPayAmount);
    invoice.creatorPayAmount = toInvoiceAmount(invoice.creatorPayAmount);
    (invoice.lineItems || []).forEach(li => { li.unitPrice = toInvoiceAmount(li.unitPrice) ?? 0; });
    return invoice;
  }

  // Every invoice. Only the top-level Invoices screen (and reports that need all of them) call
  // this; a campaign's own Invoices tab uses getInvoicesByCampaign.
  getInvoices(): Observable<Invoice[]> {
    if (this.allInvoicesLoaded) {
      return of(this.invoiceCache);
    }

    const url = `${environment.apiUrl}invoices`;
    return this.http.get<Invoice[]>(url).pipe(
      tap((invoices) => {
        (invoices || []).forEach(i => this.normalizeAmounts(i));
        this.invoiceCache = invoices || [];
        this.allInvoicesLoaded = true;
        // Later lookups come from the full list; drop the per-campaign copies so no one holds
        // instances that are not the shared cached ones.
        this.campaignInvoiceCache.clear();
      })
    );
  }

  // Invoices of one campaign. Served from the full list when that has already been loaded;
  // otherwise only this campaign's invoices are fetched (GET campaigns/{id}/invoices) - the
  // campaign screen must not pull every invoice just to show one campaign's.
  getInvoicesByCampaign(campaignId: string): Observable<Invoice[]> {
    if (this.allInvoicesLoaded) {
      return of(this.invoiceCache.filter(invoice => invoice.campaignId === campaignId));
    }

    // Callers get their own array (like the filter() above), sharing only the invoice objects,
    // so a component pushing into / removing from its list never touches the cache's list.
    const cached = this.campaignInvoiceCache.get(campaignId);
    if (cached) {
      return of([...cached]);
    }

    const url = `${environment.apiUrl}campaigns/${campaignId}/invoices`;
    return this.http.get<Invoice[]>(url).pipe(
      tap((invoices) => {
        (invoices || []).forEach(i => this.normalizeAmounts(i));
        this.campaignInvoiceCache.set(campaignId, invoices || []);
      }),
      map((invoices) => [...(invoices || [])])
    );
  }

  /**
   * Save a new invoice to the backend.
   * POSTs the provided invoice JSON to api/invoices and adds the returned invoice to the local cache.
   */
  saveNewInvoice(newInvoice: Invoice): Observable<Invoice> {
    const url = `${environment.apiUrl}invoices`;
    return this.http.post<Invoice>(url, newInvoice).pipe(
      tap((saved) => {
        // append to cache if not present
        if (saved && saved.id) {
          this.normalizeAmounts(saved);

          // Only the full list may grow; appending to it before it has been loaded would make
          // a one-invoice list look like "all invoices".
          if (this.allInvoicesLoaded) {
            this.invoiceCache = [ ...(this.invoiceCache || []), saved ];
          }

          const forCampaign = saved.campaignId ? this.campaignInvoiceCache.get(saved.campaignId) : undefined;
          if (forCampaign) {
            forCampaign.push(saved);
          }
        }
      })
    );
  }

  /**
   * Delete an invoice by id. Calls DELETE api/invoices/{id} and removes it from cache on success.
   */
  deleteInvoice(invoiceId: string): Observable<any> {
    const url = `${environment.apiUrl}invoices/${invoiceId}`;
    return this.http.delete(url).pipe(
      tap(() => {
        this.invoiceCache = (this.invoiceCache || []).filter(i => i.id !== invoiceId);
        this.campaignInvoiceCache.forEach((list, campaignId) => {
          this.campaignInvoiceCache.set(campaignId, list.filter(i => i.id !== invoiceId));
        });
      })
    );
  }

  /**
   * Patch a single top-level invoice field. Server responds 200 with no body.
   * Callers hold the same object instances as invoiceCache, so the local copy is
   * already current by the time this fires — no cache surgery needed here.
   */
  patchInvoiceField(invoiceId: string, fieldName: string, newValue: any): Observable<void> {
    const url = `${environment.apiUrl}invoices/${invoiceId}`;
    const body = {
      fieldName: fieldName,
      newValue: newValue
    };

    return this.http.patch<void>(url, body);
  }

  /**
   * Patch a single field on one line item. Stored line items have no id, so they are
   * addressed by their zero-based position in the invoice's lineItems array:
   * PATCH api/invoices/{invoiceId}/lineitems/0
   */
  patchLineItemField(invoiceId: string, index: number, fieldName: string, newValue: any): Observable<void> {
    const url = `${environment.apiUrl}invoices/${invoiceId}/lineitems/${index}`;
    const body = {
      fieldName: fieldName,
      newValue: newValue
    };

    return this.http.patch<void>(url, body);
  }

  /** Append a line item to an invoice. Returns the invoice as stored after the append. */
  addLineItem(invoiceId: string, lineItem: InvoiceLineItem): Observable<Invoice> {
    const url = `${environment.apiUrl}invoices/${invoiceId}/lineitems`;
    return this.http.post<Invoice>(url, lineItem);
  }

  /** Remove the line item at the given index. Every later line item shifts down by one. */
  removeLineItem(invoiceId: string, index: number): Observable<void> {
    const url = `${environment.apiUrl}invoices/${invoiceId}/lineitems/${index}`;
    return this.http.delete<void>(url);
  }
}
